/** @jsxImportSource @emotion/react */
import React, { FC, useCallback, useMemo } from 'react';

import arrow from '../../../../../assets/arrow_sub_up_right.svg';
import closeIcon from '../../../../../assets/icons/close-icon.svg';

import locationHeaderStyles from './location-header.styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { useIntl } from 'react-intl';
import toast from 'react-hot-toast';
import { useGetLocationByIdQuery } from '../../../../../api/locations/locations.api';
import { LocationFacilities } from '../../../../../components/location-facilities/location-facilities.component';

export const LocationHeader: FC = () => {
    const intl = useIntl();
    const isDesktop = useMediaQuery('(min-width:600px)');
    const { locationId } = useParams();
    const { data: location, isLoading: isLocationLoading } = useGetLocationByIdQuery(
        locationId ?? '',
        {
            skip: !locationId,
        }
    );
    const navigate = useNavigate();

    const address = useMemo(() => {
        if (location?.data?.address) {
            const { street_1, street_2, province, country, zip } = location?.data?.address;
            return location?.data?.address
                ? `${street_1} ${street_2 ? `, ${street_2}` : ''} ${province ? `, ${province} ` : ''}${country ? `, ${country} ` : ''}${zip}`
                : undefined;
        } else {
            return undefined;
        }
    }, [location?.data?.address]);

    const addressLink = useMemo(() => {
        return address
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(address)}`
            : undefined;
    }, [address]);

    const shareHandler = useCallback(() => {
        if (navigator.canShare()) {
            navigator.share({
                title: intl.formatMessage({ id: 'button.share.title' }),
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            toast.success(intl.formatMessage({ id: 'toast.linkCopied' }));
        }
    }, [location?.data?.name, intl]);

    if (isLocationLoading) {
        return null;
    }

    return (
        <div css={locationHeaderStyles.container}>
            {isDesktop && (
                <img
                    css={locationHeaderStyles.closeIcon}
                    src={closeIcon}
                    onClick={() => navigate(`/${window.location.search}`, { replace: true })}
                />
            )}
            <div css={locationHeaderStyles.businessCard}>
                <div css={locationHeaderStyles.topBox}>
                    <div css={locationHeaderStyles.businessCardInfo}>
                        <div css={locationHeaderStyles.businessCardInfoTitle}>
                            {location?.data?.name ?? '...'}
                        </div>
                        <div>{address ?? '...'}</div>
                        <LocationFacilities facilities={location?.data?.facilities} />
                    </div>
                    <div css={locationHeaderStyles.buttonBox}>
                        {addressLink && (
                            <span
                                onClick={shareHandler}
                                css={[
                                    locationHeaderStyles.locationHeaderButton,
                                    locationHeaderStyles.shareButton,
                                ]}>
                                {intl.formatMessage({ id: 'button.share' })}
                            </span>
                        )}
                        {addressLink && (
                            <a
                                href={addressLink}
                                target="_blank"
                                rel="noreferrer"
                                css={[
                                    locationHeaderStyles.locationHeaderButton,
                                    locationHeaderStyles.directionButton,
                                ]}>
                                <span>{intl.formatMessage({ id: 'button.direction' })}</span>
                                <img alt="arrow" src={arrow} />
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
